import type { AnySchema } from "yup";
import { object, date, number, boolean, string, array, mixed } from "yup";
import { compareDates } from "./project/helpers";
import type { useI18nContext } from "@procore/core-react";
import type { CustomField } from "@/react/prop-types";
import { CustomFieldType } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { isAfter, isBefore, startOfDay } from "date-fns";
import { isEndDateInPast } from "./helper";

// Helper function for work day validation
const workDayValidationTest = (selectedWorkDays: { [key: string]: boolean }) =>
   function (date: Date) {
      if (!date) return false;
      const day = date.getDay();
      return selectedWorkDays[day];
   };

// Shared function to generate base schema
export const getBaseSchemaForRequestAndAssignment = (
   I18n: ReturnType<typeof useI18nContext>,
   selectedWorkDays: { [key: string]: boolean },
   assignmentByAllocation: boolean,
   assignmentByTime: boolean,
   includeStatus: boolean,
   projectStartDate?: Date | null,
   projectEndDate?: Date | null,
) => ({
   project: object().required(
      I18n.t("views.company.workforce_planning.validations.required_field"),
   ),
   category: object().nullable(),
   subcategory: object().nullable(),
   start_date: date()
      .required(I18n.t("views.company.workforce_planning.validations.required_field"))
      .test(
         "validate-work-day",
         I18n.t("views.company.workforce_planning.validations.must_be_a_work_day"),
         workDayValidationTest(selectedWorkDays),
      )
      .test(
         "validate-project-start-bound",
         I18n.t("views.company.workforce_planning.validations.start_date_before_project_start"),
         function (value) {
            if (!projectStartDate || !value) return true;
            return !isBefore(startOfDay(value), startOfDay(projectStartDate));
         },
      ),
   end_date: date()
      .test(
         "validate-end-date",
         I18n.t("views.company.workforce_planning.validations.end_date_before_start_date"),
         function (endDate) {
            const { start_date: startDate } = this.parent;
            return compareDates({ startDate, endDate });
         },
      )
      .required(I18n.t("views.company.workforce_planning.validations.required_field"))
      .test(
         "validate-work-day",
         I18n.t("views.company.workforce_planning.validations.must_be_a_work_day"),
         workDayValidationTest(selectedWorkDays),
      )
      .test(
         "validate-project-end-bound",
         I18n.t("views.company.workforce_planning.validations.end_date_after_project_end"),
         function (value) {
            // Skip validation when the project end date is already in the past — the
            // pre-population logic falls back to a TBD-calculated date in that case,
            // so enforcing the boundary would block valid assignments on past-due projects.
            if (!projectEndDate || !value || isEndDateInPast(projectEndDate)) return true;
            return !isAfter(startOfDay(value), startOfDay(projectEndDate));
         },
      ),
   weeks: number()
      .default(1)
      .required(I18n.t("views.company.workforce_planning.assignment.validations.weeks_add_check"))
      .test(
         "weeks",
         I18n.t("views.company.workforce_planning.assignment.validations.weeks_greater_than_zero"),
         function (value) {
            return value > 0;
         },
      ),
   work_days: object().test(
      "validate-work-days",
      I18n.t("views.company.workforce_planning.labor_plan.validations.work_days"),
      function (values: any) {
         let isValid = false;
         Object.keys(values).forEach((i) => {
            if (values[i]) {
               isValid = true;
            }
         });
         return isValid;
      },
   ),
   ...(assignmentByTime && {
      start_time: object().required(
         I18n.t("views.company.workforce_planning.validations.required_field"),
      ),
      end_time: object().required(
         I18n.t("views.company.workforce_planning.validations.required_field"),
      ),
   }),
   ...(assignmentByAllocation && {
      percent_allocated: number().required(
         I18n.t("views.company.workforce_planning.validations.required_field"),
      ),
   }),
   ...(includeStatus && {
      status: object().required(
         I18n.t("views.company.workforce_planning.validations.required_field"),
      ),
   }),
});

export const createCustomFieldSchema = (field: CustomField) => {
   switch (field.type) {
      case CustomFieldType.BOOL:
         return boolean().nullable();
      case CustomFieldType.CURRENCY:
      case CustomFieldType.NUMBER:
         return number().nullable();
      case CustomFieldType.DATE:
         return date().nullable();
      case CustomFieldType.PARAGRAPH:
      case CustomFieldType.TEXT:
         return string().nullable();
      case CustomFieldType.SELECT:
         return object({
            id: string(),
            label: string(),
         }).nullable();
      case CustomFieldType.MULTI_SELECT:
         return array(
            object({
               id: string(),
               label: string(),
            }),
         ).nullable();
      default:
         return mixed().nullable();
   }
};

export const bulkEditAssignmentAndRequestValidationSchema = (
   I18n: ReturnType<typeof useI18nContext>,
   customFields?: CustomField[],
) =>
   object().shape({
      project: object().nullable(),
      status: object().nullable(),
      start_date: date().nullable(),
      end_date: date()
         .test(
            "validate-end-date",
            I18n.t("views.company.workforce_planning.validations.end_date_before_start_date"),
            function (endDate) {
               const { start_date: startDate } = this.parent;
               return compareDates({ startDate, endDate });
            },
         )
         .nullable(),
      start_time: object().nullable(),
      end_time: object().nullable(),
      percent_allocated: number()
         .nullable()
         .test(
            "is-positive-if-present",
            I18n.t(
               "views.company.workforce_planning.assignment.bulk.edit.validation.percent_allocation_greater_than_0",
            ),
            function (value) {
               if (value === null || value === undefined) return true;
               return value > 0;
            },
         ),
      ...(customFields && customFields.length > 0
         ? customFields.reduce((acc: Record<string, AnySchema>, field: CustomField) => {
              acc[field.name] = createCustomFieldSchema(field);
              return acc;
           }, {} as Record<string, AnySchema>)
         : {}),
   });
